/**
 * Label Configuration — a common Settings page, scoped to default and channel.
 *
 * REWRITTEN. The first version of this file was an adapted copy of Ashvini's per-courier-account
 * label screen. Per-courier is the wrong scope: the label is what the buyer and the warehouse
 * see, so it belongs to the brand and to the sales channel it shipped from. A marketplace that
 * mandates its own label format is a channel-level fact; Delhivery vs Bluedart is not. Carriers
 * constrain size and symbology, and we manage that for them.
 *
 * So: one default, plus an override per channel, with inheritance that stays live — edit the
 * default and every channel that has not been customised follows.
 *
 * Field names and grouping follow the admin panel's label customisation, so a merchant and our
 * ops team are talking about the same fields.
 */
import React, { useState } from 'react';
import {
  Box, Grid, Typography, TextField, MenuItem, Switch, Button, Alert, Chip, Divider, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import { pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, WHITE, LIGHT_BG, PRIMARY_BLUE, SUCCESS, TEAL } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { statLabelSx } from '../components/stats';
import LabelPreview from '../components/LabelPreview';
import { useLabelConfig, useConnectedChannels, LABEL_DEFAULT_SCOPE } from '../data/hooks';
import {
  PRINTER_TYPES, LABEL_SIZES, LABEL_FIELD_GROUPS, LABEL_FIELD_EXCLUSIONS,
} from '../data/defaults';
import { downloadCsv } from '../data/serviceabilityUpload';

const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const nameSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const CONTROL_HEIGHT = 34;
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE['2xs'], height: CONTROL_HEIGHT },
  '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: SIZE['3xs'] },
};

const scopeChipSx = (selected, overridden) => ({
  fontFamily: FONT,
  fontSize: SIZE['3xs'],
  fontWeight: WEIGHT.semibold,
  height: 26,
  borderRadius: '6px',
  cursor: 'pointer',
  border: `1px solid ${selected ? PRIMARY_BLUE : BORDER}`,
  bgcolor: selected ? `${PRIMARY_BLUE}14` : WHITE,
  color: selected ? PRIMARY_BLUE : SUBTLE,
  '& .MuiChip-icon': { color: overridden ? TEAL : SUBTLE, fontSize: 8, ml: 0.75 },
});

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: GAP.sm,
  px: 1,
  py: 0.35,
  borderRadius: RADIUS.sm,
  '&:hover': { bgcolor: LIGHT_BG },
};

/** Which field is blocked because the other half of an exclusive pair is on. */
const blockedBy = (key, fields) => {
  const pair = LABEL_FIELD_EXCLUSIONS.find((p) => p.includes(key));
  if (!pair) return null;
  const other = pair.find((k) => k !== key);
  return fields[other] ? other : null;
};

const fieldLabel = (key) => {
  const group = LABEL_FIELD_GROUPS.find((g) => g.fields.some((f) => f.key === key));
  return group?.fields.find((f) => f.key === key)?.label || key;
};

function LabelConfig() {
  const [scope, setScope] = useState(LABEL_DEFAULT_SCOPE);
  const {
    config, isInherited, isDefaultScope, set, toggleField, setAllInGroup, resetToDefault,
    overriddenChannels,
  } = useLabelConfig(scope);
  const { channels, isFallback: isFallbackChannels, isLoading: channelsLoading } = useConnectedChannels();
  const [saved, setSaved] = useState(false);

  const sizes = LABEL_SIZES.filter((s) => s.printer === config.printer);

  const change = (patch) => {
    set(patch);
    setSaved(false);
  };

  /** Printer type constrains size, so switching printer moves size to that printer's default. */
  const changePrinter = (printer) => {
    const first = LABEL_SIZES.find((s) => s.printer === printer);
    change({ printer, size: first.value });
  };

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='What appears on the shipping label, per sales channel'>
        Label Configuration
      </PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.md }}>
        {/* Scope — default, or a channel that overrides it */}
        <Box sx={{ ...sectionCardSx, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap' }}>
            <Typography sx={statLabelSx}>Applies to</Typography>
            <Chip
              label='All channels (default)'
              onClick={() => setScope(LABEL_DEFAULT_SCOPE)}
              sx={scopeChipSx(isDefaultScope, false)}
            />
            <Divider orientation='vertical' flexItem sx={{ borderColor: BORDER, mx: 0.5 }} />
            {channelsLoading ? (
              <Typography sx={subSx}>Loading your channels…</Typography>
            ) : (
              channels.map((channel) => {
                const overridden = overriddenChannels.includes(channel);
                return (
                  <Tooltip
                    key={channel}
                    title={overridden ? 'Has its own label settings' : 'Follows the default'}
                  >
                    <Chip
                      label={channel}
                      icon={overridden ? <span>●</span> : undefined}
                      onClick={() => setScope(channel)}
                      sx={scopeChipSx(scope === channel, overridden)}
                    />
                  </Tooltip>
                );
              })
            )}
          </Box>

          <Typography sx={{ ...subSx, mt: 0.75 }}>
            {isFallbackChannels
              ? 'Showing example channels — the live list comes from the stores your orders arrive from.'
              : 'Your connected channels. A channel only appears here once orders have come from it.'}
          </Typography>

          {!isDefaultScope && (
            <Alert
              severity={isInherited ? 'info' : 'success'}
              sx={{ mt: 1.25, fontFamily: FONT, fontSize: SIZE['2xs'], py: 0 }}
              action={
                !isInherited && (
                  <Button size='small' sx={secondaryBtnSx} startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 15 }} />} onClick={resetToDefault}>
                    Follow default again
                  </Button>
                )
              }
            >
              {isInherited
                ? 'Following the default — the first change here creates an override for this channel only.'
                : 'This channel has its own settings. Changes to the default no longer reach it.'}
            </Alert>
          )}
        </Box>

        <Grid container spacing={2} alignItems='flex-start'>
          {/* Settings */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.sm }}>
              <Box sx={sectionCardSx}>
                <Typography sx={{ ...statLabelSx, mb: 1 }}>Printer &amp; size</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select size='small' fullWidth label='Printer type' value={config.printer}
                      onChange={(e) => changePrinter(e.target.value)}
                      sx={fieldSx}
                    >
                      {PRINTER_TYPES.map((p) => (
                        <MenuItem key={p.value} value={p.value} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select size='small' fullWidth label='Label size' value={config.size}
                      onChange={(e) => change({ size: e.target.value })}
                      sx={fieldSx}
                    >
                      {sizes.map((s) => (
                        <MenuItem key={s.value} value={s.value} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Typography sx={{ ...subSx, mt: 1 }}>
                  {config.printer === 'THERMAL'
                    ? 'Thermal labels carry shipping details and no tax block.'
                    : 'Sheet labels include the tax block, and print several to a page.'}{' '}
                  Confirm your carrier accepts this size — a rejected label shows up as failed
                  pickups, not as an error here.
                </Typography>
              </Box>

              {LABEL_FIELD_GROUPS.map((group) => {
                const keys = group.fields.map((f) => f.key);
                const onCount = keys.filter((k) => config.fields[k]).length;
                return (
                  <Accordion
                    key={group.key}
                    disableGutters
                    elevation={0}
                    sx={{ border: `1px solid ${BORDER}`, borderRadius: `${RADIUS.md * 4}px !important`, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, width: '100%' }}>
                        <Typography sx={nameSx}>{group.title}</Typography>
                        <Typography sx={subSx}>
                          {onCount} of {keys.length} on
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {/*
                          One switch for the whole group instead of All on / All off buttons: two
                          buttons for a binary state read as heavier than the twelve switches they
                          govern. stopPropagation so flipping the group does not also collapse it.
                        */}
                        <Tooltip title={onCount === keys.length ? `Switch off all ${group.title.toLowerCase()}` : `Switch on all ${group.title.toLowerCase()}`}>
                          <Switch
                            size='small'
                            checked={onCount === keys.length}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { setAllInGroup(keys, e.target.checked); setSaved(false); }}
                          />
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Grid container>
                        {group.fields.map((field) => {
                          const blocker = blockedBy(field.key, config.fields);
                          return (
                            <Grid item xs={12} sm={6} key={field.key}>
                              <Box sx={rowSx}>
                                <Typography sx={{ ...nameSx, color: config.fields[field.key] ? NAVY : SUBTLE }}>
                                  {field.label}
                                </Typography>
                                <Tooltip title={blocker ? `Turning this on will switch off ${fieldLabel(blocker)}` : ''}>
                                  <Switch
                                    size='small'
                                    checked={Boolean(config.fields[field.key])}
                                    onChange={() => { toggleField(field.key, LABEL_FIELD_EXCLUSIONS); setSaved(false); }}
                                  />
                                </Tooltip>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              <Box sx={sectionCardSx}>
                <Typography sx={{ ...statLabelSx, mb: 1 }}>Custom text</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size='small' fullWidth label='Header text' value={config.headerText}
                      placeholder='Optional'
                      onChange={(e) => change({ headerText: e.target.value })}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size='small' fullWidth label='Footer text' value={config.footerText}
                      onChange={(e) => change({ footerText: e.target.value })}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: 'flex', gap: GAP.sm, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant='contained' size='small' sx={primaryBtnSx} onClick={() => setSaved(true)}>
                  Save
                </Button>
                <Button
                  size='small'
                  sx={secondaryBtnSx}
                  startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => { resetToDefault(); setSaved(false); }}
                >
                  {isDefaultScope ? 'Reset to ProShip defaults' : 'Follow default again'}
                </Button>
                {saved && (
                  <Typography sx={{ ...subSx, color: SUCCESS }}>
                    Saved. Labels generated from now on use this; already generated labels are
                    unchanged.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Live preview */}
          <Grid item xs={12} md={5}>
            <Box sx={{ ...sectionCardSx, position: 'sticky', top: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 1 }}>
                <Box>
                  <Typography sx={nameSx}>Live preview</Typography>
                  <Typography sx={subSx}>Updates as you change a setting</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Chip
                  size='small'
                  label={LABEL_SIZES.find((s) => s.value === config.size)?.label.split(' — ')[0]}
                  sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, bgcolor: LIGHT_BG, color: SUBTLE }}
                />
              </Box>

              <Box sx={{ bgcolor: LIGHT_BG, p: 1.5, borderRadius: RADIUS.md, overflowX: 'auto' }}>
                <LabelPreview config={config} />
              </Box>

              <Box sx={{ display: 'flex', gap: GAP.sm, mt: 1.25, flexWrap: 'wrap' }}>
                <Button
                  size='small'
                  sx={secondaryBtnSx}
                  startIcon={<DownloadOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() =>
                    downloadCsv(
                      `label-config-${isDefaultScope ? 'default' : scope.toLowerCase()}.csv`,
                      ['field,enabled', ...Object.entries(config.fields).map(([k, v]) => `${k},${v ? 'yes' : 'no'}`)].join('\n')
                    )
                  }
                >
                  Export settings
                </Button>
              </Box>

            </Box>
          </Grid>
        </Grid>

        {/*
          CARRIER-MANDATED LABELS, AT THE FOOT.

          It opened the page, where it was the first thing read on a screen it does not describe:
          a caveat about one carrier, above the settings it is a caveat about. Read in that order
          it suggests the page might not work before the merchant has seen what the page does.

          It still has to be said — a merchant who has carefully laid out a label needs to know
          why their ATS parcels come out different — so it sits under the settings and the
          preview, which is where a caveat belongs.
        */}
        <Alert severity='info' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.25 }}>
          <strong>Amazon Transportation Services (ATS) prints its own label.</strong> These settings
          do not apply to ATS shipments — those labels come from Amazon in their required format.
        </Alert>
      </Box>
    </Box>
  );
}

export default withLayout(LabelConfig);
